import React, { ReactNode } from 'react';

import { IntlShape } from 'react-intl';

import { SelectTypes } from '@nhl/nhl-breakaway-ui';

import CommonMessages from '~/core/commonMessages';
import { parseValueFromLocalizedAttribute } from '~/core/utils';

import { DraftPick, Filter } from './types';

import { SelectCategory, SelectContainer } from './styles';

type Option = {
    teamOptions: Array<SelectTypes.BasicOption>;
    leagueOptions: Array<SelectTypes.BasicOption>;
    amateurTeamOptions: Array<SelectTypes.BasicOption>;
    countryOptions: Array<SelectTypes.BasicOption>;
    positionOptions: Array<SelectTypes.BasicOption>;
};

export const ALL_VALUE = 'all';
export const DEFAULT_ROUND = '1';
export const PAGE_INDEX_QUERY = 'pageIndex';
export const PAGE_SIZE = 50;

export const createSecondaryOptions = (picks: Array<DraftPick>, intl: IntlShape): Option => {
    const DEFAULT_ALL: SelectTypes.BasicOption = {
        label: intl.formatMessage(CommonMessages.misc.allOptions),
        key: ALL_VALUE,
        value: ALL_VALUE,
    };

    let teamOptions: Array<SelectTypes.BasicOption> = [];
    let leagueOptions: Array<SelectTypes.BasicOption> = [];
    let amateurTeamOptions: Array<SelectTypes.BasicOption> = [];
    let countryOptions: Array<SelectTypes.BasicOption> = [];
    let positionOptions: Array<SelectTypes.BasicOption> = [];
    const teamSet = new Set();
    const leagueSet = new Set();
    const amateurTeamSet = new Set();
    const countrySet = new Set();
    const positionSet = new Set();

    const optionsSort = (options: Array<SelectTypes.BasicOption>) =>
        options.sort((option1, option2) => {
            if (option1.label && option2.label) {
                return (option1.label as string).localeCompare(option2.label as string);
            }
            if (!option1.label) {
                return 1;
            }
            if (!option2.label) {
                return -1;
            }
            return 0;
        });

    picks?.forEach((pick) => {
        if (pick.teamName) {
            const team = {
                label: parseValueFromLocalizedAttribute(pick.teamName),
                key: pick.teamId,
                value: pick.teamAbbrev?.toLowerCase(),
            };

            if (teamOptions.length === 0 || !teamSet.has(team.value)) {
                teamOptions.push(team);
                teamSet.add(team.value);
            }
        }

        //We add in this check to ensure the current pick is not a void pick. We don't want to add in undefined values
        //to our filters
        if (pick.firstName) {
            const league = {
                label: pick.amateurLeague,
                key: pick.amateurLeague,
                value: pick.amateurLeague?.toLowerCase(),
            };
            const amateurTeam = {
                label: pick.amateurClubName,
                key: pick.amateurClubName,
                value: pick.amateurClubName?.toLowerCase(),
            };
            const country = {
                label: pick.countryCode,
                key: pick.countryCode,
                value: pick.countryCode?.toLowerCase(),
            };
            const position = {
                label: pick.positionCode,
                key: pick.positionCode,
                value: pick.positionCode?.toLowerCase(),
            };

            if (leagueOptions.length === 0 || !leagueSet.has(league.value)) {
                leagueOptions.push(league);
                leagueSet.add(league.value);
            }
            if (amateurTeamOptions.length === 0 || !amateurTeamSet.has(amateurTeam.value)) {
                amateurTeamOptions.push(amateurTeam);
                amateurTeamSet.add(amateurTeam.value);
            }
            if (countryOptions.length === 0 || !countrySet.has(country.value)) {
                countryOptions.push(country);
                countrySet.add(country.value);
            }
            if (positionOptions.length === 0 || !positionSet.has(position.value)) {
                positionOptions.push(position);
                positionSet.add(position.value);
            }
        }
    });

    teamOptions = optionsSort(teamOptions);
    leagueOptions = optionsSort(leagueOptions);
    amateurTeamOptions = optionsSort(amateurTeamOptions);
    countryOptions = optionsSort(countryOptions);
    positionOptions = optionsSort(positionOptions);

    teamOptions.unshift(DEFAULT_ALL);
    leagueOptions.unshift(DEFAULT_ALL);
    amateurTeamOptions.unshift(DEFAULT_ALL);
    countryOptions.unshift(DEFAULT_ALL);
    positionOptions.unshift(DEFAULT_ALL);

    return {
        teamOptions,
        leagueOptions,
        amateurTeamOptions,
        countryOptions,
        positionOptions,
    };
};

export const formatFilters = (filters: Array<Filter>, areModalFilters?: boolean): Array<ReactNode> =>
    filters.map((filter) => (
        <SelectContainer key={filter.filterName}>
            <SelectCategory>{filter.filterLabel}</SelectCategory>
            {areModalFilters ? filter.modalFilterSelect : filter.filterSelect}
        </SelectContainer>
    ));
